import { useState } from "react";
import {
  Alert,
  Card,
  Group,
  Text,
  Badge,
  Button,
  ActionIcon,
  Tooltip,
} from "@mantine/core";
import { mapGenerationError } from "./generationError";
import { useContentPlansStore } from "./contentPlansStore";
import {
  IconAlertCircle,
  IconFileText,
  IconSparkles,
  IconExternalLink,
  IconInfoCircle,
  IconPlayerSkipForward,
  IconTrash,
} from "@tabler/icons-react";
import { modals } from "@mantine/modals";
import ContentItemDetailDrawer from "./ContentItemDetailDrawer";

export default function ContentItem({
  contentItem,
  planId,
  workstreamId,
  workstreamTitle,
  hasResearchedDossier,
}) {
  const { patchContentItem, generateContentItem, deleteContentIdea } =
    useContentPlansStore();
  const [drawerOpened, setDrawerOpened] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const errorText = mapGenerationError(contentItem.lastError);

  const handleSkip = async (e) => {
    e.stopPropagation();
    await patchContentItem(planId, workstreamId, contentItem.id, {
      status: "skipped",
    });
  };

  const handleWrite = async (e) => {
    e.stopPropagation();
    setIsGenerating(true);
    try {
      if (contentItem.status === "idea") {
        const updated = await patchContentItem(planId, workstreamId, contentItem.id, {
          status: "approvedForGeneration",
        });
        if (!updated) return;
      }
      await generateContentItem(planId, workstreamId, contentItem.id);
    } finally {
      setIsGenerating(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "approvedForGeneration":
        return "green";
      case "generating":
        return "blue";
      case "draftReadyForReview":
        return "orange";
      case "published":
        return "teal";
      case "rejected":
        return "red";
      case "skipped":
        return "gray";
      default:
        return "gray";
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "approvedForGeneration":
        return "Approved";
      case "generating":
        return "Generating...";
      case "draftReadyForReview":
        return "Draft Ready";
      case "published":
        return "Published";
      case "skipped":
        return "Skipped";
      default:
        return status;
    }
  };

  return (
    <>
      <Card
        padding="sm"
        radius="sm"
        withBorder
        style={{ cursor: "pointer" }}
        onClick={() => setDrawerOpened(true)}
      >
        <Group justify="space-between" align="flex-start">
          <div style={{ flex: 1 }}>
            <Group mb="xs" gap="xs">
              <IconFileText size={16} />
              <Text size="sm" fw={600} style={{ flex: 1 }}>
                {contentItem.title}
              </Text>
              <Tooltip label="View details">
                <ActionIcon
                  size="sm"
                  variant="subtle"
                  onClick={(e) => {
                    e.stopPropagation();
                    setDrawerOpened(true);
                  }}
                >
                  <IconInfoCircle size={14} />
                </ActionIcon>
              </Tooltip>
              {contentItem.contentDocumentId && (
                <Button
                  size="xs"
                  variant="subtle"
                  leftSection={<IconExternalLink size={12} />}
                  component="a"
                  href={`/content/${contentItem.contentDocumentId}`}
                  target="_blank"
                  onClick={(e) => e.stopPropagation()}
                >
                  Edit Draft
                </Button>
              )}
            </Group>
            <Group gap="xs" mb="xs">
              <Text size="sm" c="dimmed">
                {contentItem.primaryKeyword}
              </Text>
              {contentItem.keywordSearchVolume != null && (
                <Badge size="xs" variant="dot" color="gray">
                  {contentItem.keywordSearchVolume.toLocaleString()}/mo
                </Badge>
              )}
            </Group>
            {contentItem.angle && (
              <Text size="xs" c="dimmed" fs="italic" mb="xs">
                {contentItem.angle}
              </Text>
            )}
            {errorText && (
              <Alert
                color="red"
                variant="light"
                py="xs"
                mb="xs"
                icon={<IconAlertCircle size={14} />}
                title="Could not write this article"
              >
                <Text size="xs">{errorText}</Text>
              </Alert>
            )}
            {contentItem.scheduledDate &&
              contentItem.status !== "idea" &&
              contentItem.status !== "skipped" && (
              <Text size="sm" c="blue" mb="xs">
                Scheduled:{" "}
                {new Date(contentItem.scheduledDate).toLocaleDateString()}
              </Text>
            )}
            <Group gap="xs">
              <Badge
                size="xs"
                variant="filled"
                color={
                  contentItem.contentRole?.toLowerCase() === "pillar"
                    ? "violet"
                    : "cyan"
                }
              >
                {contentItem.contentRole?.toLowerCase() === "pillar"
                  ? "Pillar"
                  : "Support"}
              </Badge>
              <Badge size="xs" variant="light">
                {contentItem.contentType}
              </Badge>
              {contentItem.targetWordCount > 0 && (
                <Badge size="xs" variant="light" color="gray">
                  ~{contentItem.targetWordCount} words
                </Badge>
              )}
              {/* Shape, level and claim count — what makes this article different from
                  its neighbours, rather than what it has in common with them. */}
              {contentItem.archetype && (
                <Badge size="xs" variant="light" color="grape">
                  {contentItem.archetype}
                </Badge>
              )}
              {contentItem.audienceLevel && (
                <Badge size="xs" variant="outline" color="gray">
                  {contentItem.audienceLevel}
                </Badge>
              )}
              {contentItem.claimIds?.length > 0 && (
                <Tooltip label="Claims from the cluster's research this article is built on">
                  <Badge size="xs" variant="light" color="teal">
                    {contentItem.claimIds.length} claims
                  </Badge>
                </Tooltip>
              )}
            </Group>
          </div>
          <Group gap="xs">
            <Badge
              color={getStatusColor(contentItem.status)}
              variant="light"
              size="sm"
            >
              {getStatusLabel(contentItem.status)}
            </Badge>
            {(contentItem.status === "idea" ||
              contentItem.status === "approvedForGeneration") && (
              <>
                <Tooltip
                  label={
                    hasResearchedDossier
                      ? "Write from this cluster's research. A brief is not required."
                      : "Research this cluster first"
                  }
                >
                  <Button
                    size="xs"
                    color="blue"
                    leftSection={<IconSparkles size={12} />}
                    onClick={handleWrite}
                    loading={isGenerating}
                    disabled={isGenerating || !hasResearchedDossier}
                  >
                    {errorText ? "Retry" : "Write"}
                  </Button>
                </Tooltip>
                <Tooltip label="Skip">
                  <ActionIcon
                    size="sm"
                    color="gray"
                    variant="outline"
                    onClick={handleSkip}
                  >
                    <IconPlayerSkipForward size={12} />
                  </ActionIcon>
                </Tooltip>
              </>
            )}
            <Tooltip label="Delete">
              <ActionIcon
                size="sm"
                variant="subtle"
                color="red"
                onClick={(e) => {
                  e.stopPropagation();
                  modals.openConfirmModal({
                    title: "Delete content idea",
                    children: (
                      <Text size="sm">
                        Are you sure you want to delete "{contentItem.title}"?
                      </Text>
                    ),
                    labels: { confirm: "Delete", cancel: "Cancel" },
                    confirmProps: { color: "red" },
                    onConfirm: () =>
                      deleteContentIdea(planId, workstreamId, contentItem.id),
                  });
                }}
              >
                <IconTrash size={12} />
              </ActionIcon>
            </Tooltip>
          </Group>
        </Group>
      </Card>

      <ContentItemDetailDrawer
        contentItem={contentItem}
        workstreamTitle={workstreamTitle}
        planId={planId}
        workstreamId={workstreamId}
        hasResearchedDossier={hasResearchedDossier}
        opened={drawerOpened}
        onClose={() => setDrawerOpened(false)}
      />
    </>
  );
}
